import React, { Component } from 'react'
import { requestAPI } from '../../data/api'

class StateCountsGrid extends Component {
	constructor(props) {
		super(props)
		this.state = {
			gridData: [],
			loading: false,
			sortColumn: null,
			sortDirection: 'desc',
		}
	}

	componentDidMount() {
		this.fetchStateCounts()
	}

	fetchStateCounts = async () => {
		this.setState({ loading: true })
		try {
			const states = ['stage_02', 'stage_03', 'client_approval', 'contact_enrichment', 'ready_for_export']
			const response = await requestAPI(`/violations/statecounts?states=${states.join(',')}`, 'GET')

			const clientMap = {}

			if (Array.isArray(response)) {
				response.forEach(item => {
					const clientKey = item.name || item.short_name
					if (!clientMap[clientKey]) {
						clientMap[clientKey] = {
							client_name: item.name,
							short_name: item.short_name,
							stage_02_total: 0, stage_02_web: 0, stage_02_facebook: 0, stage_02_instagram: 0, stage_02_locked: 0,
							stage_03_total: 0, stage_03_web: 0, stage_03_facebook: 0, stage_03_instagram: 0, stage_03_locked: 0,
							client_approval_total: 0, client_approval_web: 0, client_approval_facebook: 0, client_approval_instagram: 0, client_approval_on_hold: 0,
							contact_enrichment_total: 0, contact_enrichment_web: 0, contact_enrichment_facebook: 0, contact_enrichment_instagram: 0, contact_enrichment_locked: 0,
							ready_for_export_total: 0, ready_for_export_web: 0, ready_for_export_facebook: 0, ready_for_export_instagram: 0,
						}
					}

					const total = Number(item.count) || 0
					const web = Number(item.web_count) || 0
					const facebook = Number(item.facebook_count) || 0
					const instagram = Number(item.instagram_count) || 0
					const locked = Number(item.locked_count) || 0
					// Cases a partner put on hold need no action, so they are reported on their own
					// and left out of the counts that describe the active workload.
					const onHold = Number(item.on_hold_count) || 0
					const webOnHold = Number(item.web_on_hold_count) || 0
					const facebookOnHold = Number(item.facebook_on_hold_count) || 0
					const instagramOnHold = Number(item.instagram_on_hold_count) || 0
					const state = item.state

					if (state === 'stage_02') {
						clientMap[clientKey].stage_02_total = total
						clientMap[clientKey].stage_02_web = web
						clientMap[clientKey].stage_02_facebook = facebook
						clientMap[clientKey].stage_02_instagram = instagram
						clientMap[clientKey].stage_02_locked = locked
					} else if (state === 'stage_03') {
						clientMap[clientKey].stage_03_total = total
						clientMap[clientKey].stage_03_web = web
						clientMap[clientKey].stage_03_facebook = facebook
						clientMap[clientKey].stage_03_instagram = instagram
						clientMap[clientKey].stage_03_locked = locked
					} else if (state === 'client_approval') {
						clientMap[clientKey].client_approval_total = total - onHold
						clientMap[clientKey].client_approval_web = web - webOnHold
						clientMap[clientKey].client_approval_facebook = facebook - facebookOnHold
						clientMap[clientKey].client_approval_instagram = instagram - instagramOnHold
						clientMap[clientKey].client_approval_on_hold = onHold
					} else if (state === 'contact_enrichment') {
						clientMap[clientKey].contact_enrichment_total = total
						clientMap[clientKey].contact_enrichment_web = web
						clientMap[clientKey].contact_enrichment_facebook = facebook
						clientMap[clientKey].contact_enrichment_instagram = instagram
						clientMap[clientKey].contact_enrichment_locked = locked
					} else if (state === 'ready_for_export') {
						clientMap[clientKey].ready_for_export_total = total
						clientMap[clientKey].ready_for_export_web = web
						clientMap[clientKey].ready_for_export_facebook = facebook
						clientMap[clientKey].ready_for_export_instagram = instagram
					}
				})
			}

			const gridData = Object.values(clientMap).sort((a, b) =>
				(a.client_name || '').localeCompare(b.client_name || '')
			)

			this.setState({ gridData, loading: false })
		} catch (error) {
			console.error('Failed to fetch state counts:', error)
			this.setState({ loading: false })
		}
	}

	handleSort = (column) => {
		const { sortColumn, sortDirection, gridData } = this.state

		let newDirection = 'desc'
		if (sortColumn === column) {
			newDirection = sortDirection === 'desc' ? 'asc' : 'desc'
		}

		const sortedData = [...gridData].sort((a, b) => {
			let aVal = a[column]
			let bVal = b[column]

			if (column === 'client_name') {
				aVal = (aVal || '').toLowerCase()
				bVal = (bVal || '').toLowerCase()
				return newDirection === 'desc'
					? bVal.localeCompare(aVal)
					: aVal.localeCompare(bVal)
			}

			aVal = Number(aVal) || 0
			bVal = Number(bVal) || 0
			return newDirection === 'desc' ? bVal - aVal : aVal - bVal
		})

		this.setState({
			gridData: sortedData,
			sortColumn: column,
			sortDirection: newDirection
		})
	}

	render() {
		const { loading, gridData, sortColumn, sortDirection } = this.state

		const getSortIndicator = (column) => {
			if (sortColumn !== column) return null
			return sortDirection === 'desc' ? ' ↓' : ' ↑'
		}

		const renderCell = (row, state) => {
			const total = row[`${state}_total`] || 0
			const fb = row[`${state}_facebook`] || 0
			const ig = row[`${state}_instagram`] || 0
			const locked = row[`${state}_locked`]
			// On hold cases are already excluded from the total above; they are shown next to it
			// so the column still tells the full story without hiding the active workload.
			const onHold = row[`${state}_on_hold`]
			return (
				<span>
					{total}
					{fb > 0 && <span style={{ color: '#1877F2', fontSize: '0.85em', marginLeft: '4px' }}>({fb} fb)</span>}
					{ig > 0 && <span style={{ color: '#C13584', fontSize: '0.85em', marginLeft: '4px' }}>({ig} ig)</span>}
					{locked > 0 && <span style={{ color: '#999', fontSize: '0.85em', marginLeft: '4px' }}>({locked} locked)</span>}
					{onHold > 0 && <span style={{ color: '#999', fontSize: '0.85em', marginLeft: '4px' }}>({onHold} on hold)</span>}
				</span>
			)
		}

		if (loading) {
			return <div className="loading-message">Loading state counts...</div>
		}

		return (
			<div>
			{(!gridData || gridData.length === 0) ? (
				<div className="empty-message">No data available</div>
			) : (
			<div className="state-counts-grid-container">
				<table className="state-counts-grid-table">
					<thead>
						<tr>
							<th className="grid-header-cell" onClick={() => this.handleSort('client_name')} style={{ cursor: 'pointer' }}>
								Client{getSortIndicator('client_name')}
							</th>
							<th className="grid-header-cell" onClick={() => this.handleSort('stage_02_total')} style={{ cursor: 'pointer' }}>
								Stage 02{getSortIndicator('stage_02_total')}
							</th>
							<th className="grid-header-cell" onClick={() => this.handleSort('stage_03_total')} style={{ cursor: 'pointer' }}>
								Stage 03{getSortIndicator('stage_03_total')}
							</th>
							<th className="grid-header-cell" onClick={() => this.handleSort('client_approval_total')} style={{ cursor: 'pointer' }}>
								Partner Review{getSortIndicator('client_approval_total')}
							</th>
							<th className="grid-header-cell" onClick={() => this.handleSort('contact_enrichment_total')} style={{ cursor: 'pointer' }}>
								Contact Enrichment{getSortIndicator('contact_enrichment_total')}
							</th>
							<th className="grid-header-cell" onClick={() => this.handleSort('ready_for_export_total')} style={{ cursor: 'pointer' }}>
								Ready For Export{getSortIndicator('ready_for_export_total')}
							</th>
						</tr>
					</thead>
					<tbody>
						{gridData.map((row, index) => (
							<tr key={index} className="grid-row">
								<td className="grid-cell client-cell">
									{row.client_name || row.name || `Client ${row.client_id}`}
									{row.short_name && (
										<span style={{ marginLeft: '8px', color: '#666', fontSize: '0.9em' }}>
											({row.short_name})
										</span>
									)}
								</td>
								<td className="grid-cell count-cell">{renderCell(row, 'stage_02')}</td>
								<td className="grid-cell count-cell">{renderCell(row, 'stage_03')}</td>
								<td className="grid-cell count-cell">{renderCell(row, 'client_approval')}</td>
								<td className="grid-cell count-cell">{renderCell(row, 'contact_enrichment')}</td>
								<td className="grid-cell count-cell">{renderCell(row, 'ready_for_export')}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			)}
			</div>
		)
	}
}

export default StateCountsGrid
